import { Component } from "react";
import {
  msalApp,
  fetchMsGraph,
  acquireToken,
  isIE,
  GRAPH_ENDPOINTS,
  GRAPH_REQUESTS
} from "~/utils/auth-utils";
import { AppContext } from "~/app-context";
import { getCurrentUserPermissions } from '~/helpers/data';

// If you support IE, our recommendation is that you sign-in using Redirect APIs
const useRedirectFlow = isIE();
const msalInteractionStatusKey = 'msal.interaction.status';

export const withAppProvider = C =>
  class AppProvider extends Component {
    constructor(props) {
      super(props);
      this.state = {
        loggedIn: false,
        account: null,
        error: null,
        graphProfile: null,
        permissions: [],
        userId: null,
        onSignIn: () => this.onSignIn(useRedirectFlow),
        onSignOut: () => this.onSignOut(),
        msalInitialized: false
      };
    }

    updateContext = (newContext) => {
      this.setState(newContext);
    }

    loadPermissions = async () => {
      const { userId, permissions } = await getCurrentUserPermissions();
      this.updateContext({ permissions, userId });
    }

    async onSignIn(redirect) {
      if (!this.state.msalInitialized) {
        console.error("MSAL is not initialized");
        this.updateContext({ error: "Authentication is not ready. Please try again in a moment." });
        return;
      }

      if (redirect) {
        return msalApp.loginRedirect(GRAPH_REQUESTS.LOGIN);
      }

      try {
        sessionStorage.removeItem(msalInteractionStatusKey);

        const loginResponse = await msalApp
          .loginPopup(GRAPH_REQUESTS.LOGIN)
          .catch(error => {
            console.error("Login failed:", error);
            this.updateContext({
              error: error.message
            });
          });

        if (loginResponse) {
          this.updateContext({
            loggedIn: true,
            account: loginResponse.account,
            error: null
          });
          await this.loadPermissions();

          const tokenResponse = await acquireToken(
            GRAPH_REQUESTS.LOGIN
          ).catch(error => {
            this.updateContext({
              error: error.message
            });
          });

          if (tokenResponse) {
            const graphProfile = await fetchMsGraph(
              GRAPH_ENDPOINTS.ME,
              tokenResponse.accessToken
            ).catch(() => {
              this.updateContext({
                error: "Unable to fetch Graph profile."
              });
            });

            if (graphProfile) {
              this.updateContext({
                graphProfile
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to sign-in:", error);
        this.updateContext({
          error: error.message
        });
      }
    }

    onSignOut() {
      msalApp.logout();
    }

    async componentDidMount() {
      try {
        await msalApp.initialize();
        this.setState({ msalInitialized: true });

        const accounts = msalApp.getAllAccounts();
        let account = null;
        if (accounts && accounts.length > 0)
          account = accounts[0];

        this.updateContext({
          loggedIn: account ? true : false,
          account
        });

        if (account) {
          await this.loadPermissions();
        }
      } catch (error) {
        console.error("Failed to initialize MSAL:", error);
        this.updateContext({
          error: "Failed to initialize authentication. Please try again."
        })
      }
    }

    render() {
      return (
        <div data-testid="app-provider-container" className="app-provider">
          <AppContext.Provider value={{ ...this.state, updateContext: this.updateContext, hasPermission: (p) => this.state.permissions.includes(p) }}>
            <C
              {...this.props}
            />
          </AppContext.Provider>
        </div>
      );
    }
  };
